import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();

  const scrollToDownload = () => {
    const element = document.getElementById('descargar');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToFeatures = () => {
    const element = document.getElementById('caracteristicas');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const heroBadge = t('hero.badge');

  return (
    <section id="inicio" className="relative isolate overflow-hidden bg-black pt-36 text-[#fffefa] sm:pt-40">
      <div className="absolute inset-x-0 top-0 -z-10 h-[840px] bg-[radial-gradient(circle_at_50%_10%,rgba(210,192,255,0.68)_0%,rgba(127,73,245,0.46)_24%,rgba(26,0,47,0.78)_52%,rgba(0,0,0,0)_78%)]" />
      <div className="absolute inset-x-0 top-20 -z-10 mx-auto h-[560px] max-w-6xl rounded-full bg-[linear-gradient(135deg,#d2c0ff_0%,#7f49f5_44%,#1a002f_100%)] opacity-70 blur-3xl" />
      <div className="absolute left-1/2 top-44 -z-10 h-64 w-[80rem] -translate-x-1/2 rotate-[-8deg] bg-[linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,0.16),rgba(10,132,174,0.18),rgba(255,255,255,0))] blur-2xl" />
      <div className="absolute inset-x-0 top-[690px] -z-10 h-48 bg-gradient-to-b from-transparent to-black" />

      <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-3 rounded-full bg-black/32 px-3 py-2 text-xs text-[#d8d6e3] shadow-[0_18px_70px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur">
            <span className="rounded-sm border border-white/12 bg-white/[0.08] px-2 py-1 font-medium uppercase text-[#d2c0ff]">{t('hero.badgePill')}</span>
            <span className="font-normal">{heroBadge}</span>
            <svg className="h-3.5 w-3.5 text-[#c9c8d0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0-4 4m4-4H3" />
            </svg>
          </div>

          <h1 className="mx-auto mt-8 max-w-5xl text-[40px] font-normal leading-[1.04] tracking-normal text-white sm:text-6xl lg:text-[76px]">
            {t('hero.title1')}{' '}
            <span className="bg-gradient-to-r from-white via-[#efe8ff] to-[#d2c0ff] bg-clip-text text-transparent">
              {t('hero.titleHighlight')}
            </span>{' '}
            {t('hero.title2')}
          </h1>

          <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[#d7d6de] sm:text-xl">
            {t('hero.description')}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={scrollToDownload}
              className="flex min-h-[48px] w-full min-w-[218px] items-center justify-center gap-2 rounded-md bg-white px-8 py-3 text-base font-medium text-black shadow-[inset_0_-16px_24px_rgba(255,255,255,0.48),0_4px_20px_rgba(255,255,255,0.18)] transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0 4-4m-4 4-4-4M5 20h14" />
              </svg>
              <span>{t('hero.downloadButton')}</span>
            </button>
            <button
              onClick={scrollToFeatures}
              className="flex min-h-[48px] w-full min-w-[218px] items-center justify-center gap-2 rounded-md border border-white/[0.18] bg-white/[0.06] px-8 py-3 text-base font-medium text-[#fffefa] shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] backdrop-blur transition-colors duration-200 hover:bg-white/[0.1] sm:w-auto"
            >
              <span>{t('header.features')}</span>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0-4 4m4-4H3" />
              </svg>
            </button>
          </div>

        </div>

        <div className="relative mx-auto mt-20 max-w-5xl overflow-hidden rounded-[30px] border border-white/10 bg-[#0b0b0f] shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_32px_100px_rgba(0,0,0,0.52)]">
          <div className="pointer-events-none absolute -left-36 top-4 h-96 w-96 rounded-full bg-[#7f49f5]/25 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#d5aa42]/15 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:linear-gradient(to_bottom,black,transparent_90%)]" />

          <div className="relative flex min-h-[650px] items-center justify-center px-6 py-10 sm:min-h-[760px] sm:py-12">
            <div className="relative flex min-h-[600px] w-full items-center justify-center sm:min-h-[700px]">
              <div className="pointer-events-none absolute h-[76%] w-[76%] rounded-full border border-white/[0.06]" />
              <div className="pointer-events-none absolute h-[58%] w-[58%] rounded-full border border-white/[0.08]" />
              <div className="pointer-events-none absolute bottom-8 h-20 w-64 rounded-full bg-black/80 blur-2xl" />

              <div className="relative h-[600px] w-[277px] sm:h-[700px] sm:w-[323px]">
                <div className="absolute inset-0 rounded-[3.2rem] border border-white/[0.16] bg-[linear-gradient(145deg,#303037_0%,#0a0a0d_38%,#202026_100%)] p-2 shadow-[0_36px_100px_rgba(0,0,0,0.72)]">
                  <div className="h-full overflow-hidden rounded-[2.72rem] bg-black">
                    <img
                      src="/farm-screenshot.png"
                      alt="FARM app dashboard"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
